import React, { useRef, useEffect, useCallback, useState } from 'react';

// --- Types ---
interface Particle {
    x: number;
    y: number;
}
interface Velocity {
    x: number;
    y: number;
}
interface CharInfo {
    char: string;
    x: number;
    y: number;
    color: string;
}

// --- Configuration (Adjust as needed) ---
const config = {
    mouseRadius: 39,
    intensity: 12,
    fontSize: 12, // Base font size in px
    charSpacing: 0.6, // Multiplier for character spacing (can affect column calculation)
    lineHeight: 1, // Multiplier for line height
    mousePersistence: 0.97, // How quickly mouse effect fades (0-1)
    returnSpeed: 0.33, // How quickly particles return to original position (0-1)
    returnWhenStill: true, // Should particles return when mouse is still?
    enableJiggle: true,
    jiggleIntensity: 0.2,
    detailFactor: 80, // Higher = more columns/rows (potentially slower)
    contrast: 50, // 0-200 (100 is normal)
    brightness: 120, // 0-200 (100 is normal)
    saturation: 170, // (Currently unused in default colorScheme, but kept for potential use)
    useTransparentBackground: true,
    backgroundColor: "transparent",
};

// --- Character Set ---
const charSet = " .:-=+*#%@";

// --- Color Scheme Function ---
const colorScheme = (r: number, g: number, b: number, brightness: number): string => {
    const neonIntensity = 2; // Adjust for stronger/weaker neon glow
    const threshold = 128; // Brightness threshold to start glowing

    const neonR = brightness < threshold ? 0 : (brightness - threshold) * neonIntensity * (r / Math.max(1, brightness));
    const neonG = brightness < threshold ? 0 : (brightness - threshold) * neonIntensity * (g / Math.max(1, brightness));
    const neonB = brightness < threshold ? 0 : (brightness - threshold) * neonIntensity * (b / Math.max(1, brightness));

    return `rgb(${Math.min(255, Math.round(neonR))}, ${Math.min(255, Math.round(neonG))}, ${Math.min(255, Math.round(neonB))})`;
};

// --- Base64 Image Source ---
// Simple 100x100 black and white gradient pattern
const base64ImageSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAIAAAD/gAIDAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA2ZpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMC1jMDYwIDYxLjEzNDc3NywgMjAxMC8wMi8xMi0xNzozMjowMCAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0UmVmPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VSZWYjIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0ieG1wLmRpZDowMTgwMTE3NDA3MjA2ODExOEY2MkQyNTFFNDI0N0U3NSIgeG1wTU06RG9jdW1lbnRJRD0ieG1wLmRpZDo5NzVEOTA1QzQ5MjMxMUUyOTAzM0EyODQxRkI1ODVFNCIgeG1wTU06SW5zdGFuY2VJRD0ieG1wLmlpZDo5NzVEOTA1QjQ5MjMxMUUyOTAzM0EyODQxRkI1ODVFNCIgeG1wOkNyZWF0b3JUb29sPSJBZG9iZSBQaG90b3Nob3AgQ1M1IE1hY2ludG9zaCI+IDx4bXBNTTpEZXJpdmVkRnJvbSBzdFJlZjppbnN0YW5jZUlEPSJ4bXAuaWlkOjAxODAxMTc0MDcyMDY4MTE4RjYyRDI1MUU0MjQ3RTc1IiBzdFJlZjpkb2N1bWVudElEPSJ4bXAuZGlkOjAxODAxMTc0MDcyMDY4MTE4RjYyRDI1MUU0MjQ3RTc1Ii8+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+2JwdZQAAAGxJREFUeNrs2sEJACAMA0DRzvs03Yp/RZAUH2lhHoWQi5DIr+8+9WxwZ+DChVyIhUAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEAIhEBIOW0BBgDqyQF+yplZvwAAAABJRU5ErkJggg==";

// --- React Component ---
const LoadingScreen: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null); // Ref for the container div
    const animationFrameId = useRef<number | null>(null);
    const imageRef = useRef<HTMLImageElement>(new Image()); // Use an Image object directly

    // Store animation state in refs to avoid re-creating them on every render
    const particlesRef = useRef<Particle[]>([]);
    const velocitiesRef = useRef<Velocity[]>([]);
    const originalPositionsRef = useRef<Particle[]>([]);
    const charsRef = useRef<CharInfo[]>([]);
    const mousePosRef = useRef<{ x: number; y: number; lastMove: number }>({ x: -1000, y: -1000, lastMove: 0 });
    const dimensionsRef = useRef<{ width: number, height: number, cols: number, rows: number }>({ width: 0, height: 0, cols: 0, rows: 0 });
    const isInitializedRef = useRef<boolean>(false);

    // --- Canvas Update and Image Processing ---
    const updateCanvasSize = useCallback((): boolean => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        const image = imageRef.current;
        if (!container || !canvas || !image.naturalWidth) return false; // Ensure image is loaded

        const containerWidth = container.clientWidth || 300;
        const containerHeight = container.clientHeight || 150;
        const mediaRatio = image.naturalHeight / image.naturalWidth;

        let width, height;
        if (containerWidth * mediaRatio <= containerHeight) {
            width = containerWidth;
            height = width * mediaRatio;
        } else {
            height = containerHeight;
            width = height / mediaRatio;
        }

        // Only resize if dimensions actually changed significantly
        if (Math.abs(canvas.width - width) > 1 || Math.abs(canvas.height - height) > 1) {
            canvas.width = width;
            canvas.height = height;
            dimensionsRef.current.width = width;
            dimensionsRef.current.height = height;
            return true; // Indicate size changed
        }
        return false; // Size did not change

    }, []);

    const applyContrastAndBrightness = useCallback((imageData: ImageData): ImageData => {
        const { contrast: contrastPercent, brightness: brightnessPercent } = config;
        const data = imageData.data;

        if (contrastPercent === 100 && brightnessPercent === 100) return imageData;

        const contrastFactor = contrastPercent <= 100 ? contrastPercent / 100 : 1 + (contrastPercent - 100) / 100 * 0.8;
        const brightnessFactor = brightnessPercent <= 100 ? (brightnessPercent / 100) * 1.2 : 1 + (brightnessPercent - 100) / 100 * 0.8;

        for (let i = 0; i < data.length; i += 4) {
            let r = data[i];
            let g = data[i + 1];
            let b = data[i + 2];

            // Apply Brightness
             if (brightnessPercent !== 100) {
                if (brightnessPercent < 100) {
                    r *= brightnessFactor;
                    g *= brightnessFactor;
                    b *= brightnessFactor;
                } else {
                    r += (255 - r) * (brightnessFactor - 1);
                    g += (255 - g) * (brightnessFactor - 1);
                    b += (255 - b) * (brightnessFactor - 1);
                }
            }

            // Apply Contrast
            if (contrastPercent !== 100) {
                r = 128 + contrastFactor * (r - 128);
                g = 128 + contrastFactor * (g - 128);
                b = 128 + contrastFactor * (b - 128);
            }

            data[i] = Math.max(0, Math.min(255, r));
            data[i + 1] = Math.max(0, Math.min(255, g));
            data[i + 2] = Math.max(0, Math.min(255, b));
        }
        return imageData;
    }, []);


    const generateAsciiArt = useCallback((forceRegenerateParticles: boolean = false) => {
        const canvas = canvasRef.current;
        const image = imageRef.current;
        const { width, height } = dimensionsRef.current;

        if (!canvas || !image.naturalWidth || width === 0 || height === 0) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Determine columns based on width and detail factor
        const baseFontSize = config.fontSize; // Use config font size
        const desiredCharWidth = baseFontSize * config.charSpacing;
        const columns = Math.round(Math.max(20, (width / desiredCharWidth) * (config.detailFactor / 100)));

        // Ensure columns is an integer
        const finalColumns = Math.max(10, Math.floor(columns));

        const aspectRatio = image.naturalHeight / image.naturalWidth;
        // Calculate rows based on aspect ratio and maintain character aspect ratio (font size vs line height)
        const charHeight = baseFontSize * config.lineHeight;
        const rows = Math.max(10, Math.floor((height / charHeight) * (width / (finalColumns * desiredCharWidth)) * aspectRatio));

        // Store calculated cols/rows
        dimensionsRef.current.cols = finalColumns;
        dimensionsRef.current.rows = rows;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = finalColumns;
        tempCanvas.height = rows;
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return;

        tempCtx.drawImage(image, 0, 0, finalColumns, rows);
        let imageData = tempCtx.getImageData(0, 0, finalColumns, rows);
        imageData = applyContrastAndBrightness(imageData);

        const actualCharWidth = width / finalColumns;
        const actualCharHeight = height / rows; // Use calculated height/rows for Y positioning

        // Initialize or re-initialize particles if needed
        if (forceRegenerateParticles || particlesRef.current.length !== finalColumns * rows) {
            particlesRef.current = [];
            velocitiesRef.current = [];
            originalPositionsRef.current = [];
            charsRef.current = [];
            for (let y = 0; y < rows; y++) {
                for (let x = 0; x < finalColumns; x++) {
                    // Center characters within their calculated cell
                    const posX = (x + 0.5) * actualCharWidth;
                    const posY = (y + 0.5) * actualCharHeight;
                    particlesRef.current.push({ x: posX, y: posY });
                    velocitiesRef.current.push({ x: 0, y: 0 });
                    originalPositionsRef.current.push({ x: posX, y: posY });
                    charsRef.current.push({ char: ' ', x: posX, y: posY, color: 'black' });
                }
            }
        }

        // Update character data
        const pixels = imageData.data;
        for (let y = 0; y < rows; y++) {
            for (let x = 0; x < finalColumns; x++) {
                const pixelIndex = (y * finalColumns + x) * 4;
                const r = pixels[pixelIndex];
                const g = pixels[pixelIndex + 1];
                const b = pixels[pixelIndex + 2];
                const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
                const charIndex = Math.floor((brightness / 255) * (charSet.length - 1));
                const char = charSet[Math.min(charIndex, charSet.length - 1)];
                const color = colorScheme(r, g, b, brightness);

                const charIdx = y * finalColumns + x;
                if (charIdx < charsRef.current.length) {
                    charsRef.current[charIdx].char = char;
                    charsRef.current[charIdx].color = color;
                }
            }
        }
    }, [applyContrastAndBrightness]);

    // --- Animation Loop ---
    const animate = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { width, height, cols } = dimensionsRef.current;
        const particles = particlesRef.current;
        const velocities = velocitiesRef.current;
        const originalPositions = originalPositionsRef.current;
        const chars = charsRef.current;
        const mousePos = mousePosRef.current;

        ctx.clearRect(0, 0, width, height);

        if (!config.useTransparentBackground) {
            ctx.fillStyle = config.backgroundColor;
            ctx.fillRect(0, 0, width, height);
        }

        const charWidth = width / cols;
        const dynamicFontSize = Math.min(config.fontSize, charWidth / config.charSpacing);

        ctx.font = `${dynamicFontSize}px monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const mouseStillTime = Date.now() - mousePos.lastMove;
        const mouseIsStill = mouseStillTime > 500;

        for (let i = 0; i < particles.length; i++) {
            const particle = particles[i];
            const velocity = velocities[i];
            const targetX = originalPositions[i].x;
            const targetY = originalPositions[i].y;

            const dx = particle.x - mousePos.x;
            const dy = particle.y - mousePos.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < config.mouseRadius && (!mouseIsStill || !config.returnWhenStill)) {
                const force = (1 - distance / config.mouseRadius) * config.intensity;
                const angle = Math.atan2(dy, dx);
                velocity.x += Math.cos(angle) * force * 0.2;
                velocity.y += Math.sin(angle) * force * 0.2;
            }

            if (config.enableJiggle) {
                velocity.x += (Math.random() - 0.5) * config.jiggleIntensity;
                velocity.y += (Math.random() - 0.5) * config.jiggleIntensity;
            }

            velocity.x *= config.mousePersistence;
            velocity.y *= config.mousePersistence;

            particle.x += velocity.x;
            particle.y += velocity.y;

            const springX = targetX - particle.x;
            const springY = targetY - particle.y;
            particle.x += springX * config.returnSpeed;
            particle.y += springY * config.returnSpeed;

            const charInfo = chars[i];
            if (charInfo) {
                ctx.fillStyle = charInfo.color;
                ctx.fillText(charInfo.char, particle.x, particle.y);
            }
        }

        animationFrameId.current = requestAnimationFrame(animate);
    }, []);

    // --- Event Handlers ---
    const handleMouseMove = useCallback((event: MouseEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        mousePosRef.current = {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
            lastMove: Date.now(),
        };
    }, []);

    const handleMouseLeave = useCallback(() => {
        mousePosRef.current = {
            x: -1000,
            y: -1000,
            lastMove: mousePosRef.current.lastMove,
        };
    }, []);

    const handleResize = useCallback(() => {
        if (updateCanvasSize()) {
           generateAsciiArt(true);
        }
    }, [updateCanvasSize, generateAsciiArt]);

    // --- Initialization Effect ---
    useEffect(() => {
        const image = imageRef.current;
        let canvas = canvasRef.current;

        const init = () => {
            if (!canvas) {
                console.error("Canvas element not found during init.");
                return;
            }
            if (isInitializedRef.current) return;

            if (updateCanvasSize()) {
                generateAsciiArt(true);
            } else {
                generateAsciiArt(true);
            }

            canvas.addEventListener('mousemove', handleMouseMove);
            canvas.addEventListener('mouseleave', handleMouseLeave);
            window.addEventListener('resize', handleResize);

            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
            animationFrameId.current = requestAnimationFrame(animate);
            isInitializedRef.current = true;
        };

        image.onload = init;
        image.onerror = () => {
            console.error("Failed to load the source image, using fallback");
            // Continue initialization even if image fails to load
            init();
        };
        image.src = base64ImageSrc;

        if (image.complete && image.naturalWidth > 0) {
           init();
        }

        return () => {
            isInitializedRef.current = false;
            if (canvas) {
                canvas.removeEventListener('mousemove', handleMouseMove);
                canvas.removeEventListener('mouseleave', handleMouseLeave);
            }
            window.removeEventListener('resize', handleResize);

            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
                animationFrameId.current = null;
            }

            particlesRef.current = [];
            velocitiesRef.current = [];
            originalPositionsRef.current = [];
            charsRef.current = [];
        };
    }, [animate, generateAsciiArt, handleMouseMove, handleMouseLeave, handleResize, updateCanvasSize]);

    return (
        <div ref={containerRef} className="min-h-screen bg-background text-foreground font-mono flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
                 <canvas
                     ref={canvasRef}
                     style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
                     aria-label="ASCII art loading animation"
                 />
            </div>
            <div className="absolute bottom-1/4 text-secondary z-10">
                Initializing system...
            </div>
        </div>
    );
};

export default LoadingScreen;